import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import isEmpty from 'lodash/isEmpty';

import { useDispatch } from 'store/configureStore';

import {
  fetchCountries,
  fetchRecipientByCode,
  fetchRecipientCountries,
} from 'actions';
import { InvalidRecipient } from 'components/InvalidRecipient/InvalidRecipient';
import { Spinner } from 'components/Spinner/Spinner';
import { Widget } from 'components/Widget/Widget';
import type { RootState } from 'reducers/types';
import {
  entityHasErrors,
  getCountries,
  getRecipientCountries,
  getRecipientId,
} from 'selectors';

function WidgetSetup() {
  const [isFetching, setIsFetching] = useState(true);
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  const countries = useSelector((state: RootState) => getCountries(state));
  const recipientCountries = useSelector((state: RootState) =>
    getRecipientCountries(state),
  );
  const recipientCode = useSelector((state: RootState) =>
    getRecipientId(state),
  );
  const hasRecipientErrors = useSelector((state: RootState) =>
    entityHasErrors(state, 'recipient'),
  );

  const subdomain = searchParams.get('subdomain');
  const recipient =
    searchParams.get('recipient') || searchParams.get('destination');

  const areCountriesFetched = useMemo(() => !isEmpty(countries), [countries]);
  const areRecipientCountriesFetched = useMemo(
    () => !isEmpty(recipientCountries),
    [recipientCountries],
  );

  function addWidgetStyles() {
    // @ts-expect-error not sure
    import('./WidgetSetup.scss');
  }

  useEffect(() => {
    if (areCountriesFetched && areRecipientCountriesFetched && isFetching) {
      setIsFetching(false);
    }

    if (recipientCode && isFetching) {
      dispatch(fetchRecipientCountries(recipientCode));
    }
  }, [
    areCountriesFetched,
    areRecipientCountriesFetched,
    recipientCode,
    dispatch,
    isFetching,
  ]);

  useEffect(() => {
    addWidgetStyles();
    dispatch(fetchCountries('en'));

    if (subdomain) {
      dispatch(fetchRecipientByCode(subdomain));
    } else if (recipient) {
      dispatch(fetchRecipientByCode(recipient));
    }
  }, []); // eslint-disable-line

  if (hasRecipientErrors) {
    return <InvalidRecipient />;
  }
  if (isFetching) {
    return <Spinner />;
  }

  return <Widget />;
}

export { WidgetSetup };
